/**
 * Pure DOM rendering of the run list / job list from a store snapshot.
 *
 * These functions build DOM nodes from a `RunState` snapshot and return them;
 * they do not touch the store or relay layer, so they are easy to test and
 * reason about. All keys are derived from stable run/job ids — never
 * `Math.random()` (Gate 2.6b).
 */
import type { RunState } from './store';
import { runDisplayConclusion } from './store';
import type { Job } from './types';
import { conclusionClass, relativeTime, summarizeRunError, formatRunError, isFailure, firstMeaningfulLine, fallbackErrorLine } from './lib/parse';
import { el, pill, truncateId, conclusionIcon } from './components/runs-list';

/** Header metadata for the dashboard shell. */
export interface HeaderInfo {
  relays: readonly string[];
  coordinatorPubkey: string;
  connected: boolean;
  /** Invoked when the user clicks the Reconnect button (only shown when disconnected). */
  onReconnect?: () => void;
}

/** Build the dashboard header: relay names + truncated coordinator + live-dot. */
export function renderHeader(info: HeaderInfo): HTMLElement {
  const header = el('header', 'dash-header');

  const relays = el('span', 'header-relays', info.relays.join(' · '));
  const coord = el('span', 'header-coordinator', `coordinator ${truncateId(info.coordinatorPubkey)}`);
  const dot = el('span', `live-dot ${info.connected ? 'live' : 'down'}`);
  dot.title = info.connected ? 'connected' : 'disconnected';

  header.append(relays, coord, dot);

  // When disconnected, show a connection-lost indicator and a Reconnect button
  // so the user can manually re-establish the subscription (T6).
  if (!info.connected) {
    const lost = el('span', 'conn-lost', 'connection lost');
    header.append(lost);
    const btn = el('button', 'reconnect-btn', 'Reconnect');
    btn.type = 'button';
    if (info.onReconnect) {
      btn.addEventListener('click', () => info.onReconnect?.());
    }
    header.append(btn);
  }

  return header;
}

/**
 * Mount the dashboard header into `container` and return a setter that swaps in
 * a freshly rendered header whenever the connection state changes (T6).
 *
 * The LIVE header node is held in a closure variable and reassigned on every
 * swap. This matters: `Element.replaceWith` is a no-op on a node that is no
 * longer in the DOM, so capturing the boot-time node once (e.g. in a `const`)
 * freezes the live-dot / "connection lost" indicator / Reconnect button at the
 * first transition's state and makes every later transition invisible.
 */
export function mountHeader(container: HTMLElement, info: HeaderInfo): (connected: boolean) => void {
  let current = renderHeader(info);
  container.append(current);
  return (connected: boolean) => {
    const next = renderHeader({ ...info, connected });
    // Replace the node that is CURRENTLY in the DOM, then adopt the new node:
    // `current` must be reassigned, otherwise the next replaceWith targets a
    // detached node and silently does nothing.
    current.replaceWith(next);
    current = next;
  };
}

/** Shown when an event carried no `a` tag — never render a blank/empty row. */
const UNKNOWN_REPO = 'unknown repo';
/** Shown when an event carried no `refs/...` `r` tag. */
const UNKNOWN_BRANCH = 'unknown branch';

/**
 * Build the `repo @ branch` label of a run row (`.run-repo-branch` wrapping
 * `.run-repo` and `.run-branch`, so both are separately addressable and
 * stable). Unknown repo/branch renders a readable placeholder instead of an
 * empty element; the raw repo address and ref are kept as tooltips.
 */
export function renderRepoBranch(run: RunState): HTMLElement {
  const wrap = el('span', 'run-repo-branch');

  const repo = el('span', 'run-repo', run.repoId || UNKNOWN_REPO);
  if (run.repoAddr) repo.title = run.repoAddr;
  if (!run.repoId) repo.classList.add('unknown');

  const branch = el('span', 'run-branch', run.branch || UNKNOWN_BRANCH);
  if (run.ref) branch.title = run.ref;
  if (!run.branch) branch.classList.add('unknown');

  wrap.append(repo, document.createTextNode(' @ '), branch);
  return wrap;
}

/** Build a single run row element. */
export function renderRunRow(
  run: RunState,
  nowMs: number = Date.now(),
  selectedRunId?: string,
  onSelect?: (runId: string) => void,
): HTMLElement {
  const row = el('div', 'run-row');
  row.dataset.runId = run.runId;
  row.dataset.testid = 'run-row';
  if (selectedRunId === run.runId) row.classList.add('selected');

  const status = pill(`run-status status-${run.status}`, run.status);

  const conclusion = runDisplayConclusion(run);
  const badge = pill(`conclusion-badge conclusion-${conclusionClass(conclusion)}`, conclusion ?? 'pending');

  // WHICH repo, WHICH branch — the first thing the operator asked to see.
  const repoBranch = renderRepoBranch(run);

  row.append(repoBranch, status, badge);

  // Failure surfacing (D2b): a failed run shows a short error summary on the
  // row itself — failing job + exit code + first meaningful error line — so
  // the operator never has to open the job pane to know what broke.
  const error = summarizeRunError({ conclusion: run.conclusion, jobs: run.jobs.values() });
  if (error) {
    row.append(el('span', 'run-error', formatRunError(error)));
  }

  // Run title / id: the run id is the stable identifier shown as the title.
  const title = el('span', 'run-title', run.runId);

  const commit = el('code', 'run-commit', run.shortCommit);

  const trigger = el('span', 'run-trigger', run.trigger ?? '');

  const time = el('time', 'run-time', relativeTime(run.updatedAt, nowMs));

  row.append(title, commit, trigger, time);

  // Concluded runs get a conclusion icon/color.
  if (conclusion) {
    row.append(conclusionIcon(conclusion));
  }

  if (onSelect) {
    row.addEventListener('click', () => onSelect(run.runId));
  }
  return row;
}

/** Build the job list pane for a single run (GitHub Actions "run -> jobs"). */
export function renderJobListPane(run: RunState, nowMs: number = Date.now()): HTMLElement {
  const pane = el('div', 'job-list-pane');
  pane.dataset.runId = run.runId;
  pane.dataset.testid = 'job-pane';

  const heading = el('h3', 'job-list-heading', 'Jobs');
  pane.append(heading);

  const jobs = [...run.jobs.values()].sort((a, b) => a.id.localeCompare(b.id));
  if (jobs.length === 0) {
    pane.append(el('p', 'job-empty', 'No jobs yet — waiting for job results…'));
    return pane;
  }

  const list = el('ul', 'job-list');
  for (const job of jobs) {
    list.append(renderJobRow(job, nowMs));
  }
  pane.append(list);
  return pane;
}

/** Build a single job row within the job list pane. */
export function renderJobRow(job: Job, nowMs: number = Date.now()): HTMLElement {
  const row = el('li', 'job-row');
  row.dataset.jobId = job.id;
  row.dataset.testid = 'job-row';

  const name = el('span', 'job-name', job.name ?? job.jobId ?? job.id);

  const status = pill(`job-status status-${job.status}`, job.status);
  row.append(name, status);

  // Live status: spinner for in_progress, distinct from concluded.
  if (job.status === 'in_progress') {
    row.append(el('span', 'job-spinner', '◌'));
  }

  if (job.conclusion) {
    row.append(pill(`conclusion-badge conclusion-${conclusionClass(job.conclusion)}`, job.conclusion));
  }

  if (job.exitCode !== undefined) {
    row.append(el('code', 'job-exit', `exit ${job.exitCode}`));
  }

  if (job.runsOn) {
    row.append(el('span', 'job-runs-on', job.runsOn));
  }

  if (job.queuedAt !== undefined) {
    row.append(el('time', 'job-queued', `queued ${relativeTime(job.queuedAt, nowMs)}`));
  }
  if (job.startedAt !== undefined) {
    row.append(el('time', 'job-started', `started ${relativeTime(job.startedAt, nowMs)}`));
  }

  // Failed jobs get a collapsible log-tail section sourced from the 9841
  // content, EXPANDED by default (D2a) so the failure is visible without
  // hunting; the summary still toggles it closed.
  if (job.conclusion === 'failure' && job.logTail) {
    const details = el('details', 'job-log-details');
    details.setAttribute('open', '');
    const summary = el('summary', 'log-toggle', 'log tail');
    const tail = el('pre', 'job-log-tail', job.logTail);
    details.append(summary, tail);
    row.append(details);
  } else if (isFailure(job.conclusion)) {
    // No log tail at all (e.g. `startup_failure`): still show a readable line.
    const line = firstMeaningfulLine(job.logTail) || fallbackErrorLine(job.conclusion);
    row.append(el('span', 'job-error', line));
  }

  return row;
}

/** Render the full run list into a container element. */
export function renderRunList(
  runs: readonly RunState[],
  nowMs: number = Date.now(),
  selectedRunId?: string,
  onSelect?: (runId: string) => void,
): HTMLElement {
  const container = el('div', 'run-list');
  container.dataset.testid = 'runs-list';

  if (runs.length === 0) {
    const empty = el('p', 'empty-state', 'No CI runs yet — waiting for coordinator events…');
    container.append(empty);
    return container;
  }

  for (const run of runs) {
    const row = renderRunRow(run, nowMs, selectedRunId, onSelect);
    container.append(row);
  }
  return container;
}
